/**
 * Planning-project catalogue (public/data/doan.json, built by scripts/build-doan.py).
 * Lists every approved plan of Hà Nội we know of: the ones digitised on the city's planning
 * information system (matched to the live list through `pid`) and the ones that only exist as a
 * published decision (Ministry of Construction portal, Hanoi Urban Planning Institute), with
 * corrected codes, decisions, document links and an approximate extent for the latter.
 */
import { getJSON, api } from './api.js';
import { vurl } from './assetv.js';
import { fold } from './core/text.js';
import { normCode } from './core/intent.js';
import { toPolygons, polygonArea, interiorPoint } from './core/geo.js';

export const KINDS = [
  { id: 'qhc-tp', title: 'Quy hoạch chung Thủ đô', short: 'QH chung Thủ đô', icon: 'map' },
  { id: 'qhc-dtvt', title: 'Quy hoạch chung đô thị vệ tinh', short: 'Đô thị vệ tinh', icon: 'orbit' },
  { id: 'qhc-huyen', title: 'Quy hoạch chung huyện, thị xã', short: 'QH chung huyện', icon: 'landmark' },
  { id: 'qhc-tt', title: 'Quy hoạch chung thị trấn, đô thị', short: 'QH chung thị trấn', icon: 'building' },
  { id: 'qhpk', title: 'Quy hoạch phân khu', short: 'Phân khu', icon: 'layout-grid' },
  { id: 'qhct', title: 'Quy hoạch chi tiết', short: 'Chi tiết 1/500', icon: 'blocks' },
];
export const KIND = Object.fromEntries(KINDS.map((k) => [k.id, k]));
const KIND_OF_LAYER = { 659: 'qhpk', 674: 'qhct', 670: 'qhc-tp' };

let doc = null;
let pending = null;
let live = null;        // Map pid -> official project (with geometry), once api.projects() answers
let livePending = null;
let merged = null;      // catalogue items + official projects missing from the catalogue
const byPid = new Map();

/** Fetch the catalogue once (small static file, cached by the browser / CDN). */
export function loadCatalog() {
  if (doc) return Promise.resolve(doc);
  if (!pending) {
    pending = getJSON(vurl('/data/doan.json', 'doan'), { track: false, timeout: 20000 }).then((j) => {
      doc = { built: j.built || null, sources: j.sources || [], items: (j.items || []).filter((x) => x && x.id && x.name) };
      for (const it of doc.items) if (it.pid) byPid.set(it.pid, it);
      merged = null;
      return doc;
    }).catch((e) => { pending = null; throw e; });
  }
  return pending;
}
export const catalogDoc = () => doc;

/** The live list of digitised projects (geometry for the boundary highlight). */
export function loadLive() {
  if (live) return Promise.resolve(live);
  if (!livePending) {
    livePending = api.projects().then((list) => { live = new Map(list.map((p) => [p.id, p])); merged = null; return live; })
      .catch((e) => { livePending = null; throw e; });
  }
  return livePending;
}
export const liveProject = (e) => (e?.pid && live?.get(e.pid)) || null;
export const liveReady = () => !!live;

/** Catalogue entry of an official project (by its id), or null. */
export const entryOf = (pid) => (pid && byPid.get(pid)) || null;

/** All entries: the catalogue plus any digitised project published after the catalogue was built. */
export function entries() {
  if (!doc) return [];
  if (merged) return merged;
  const out = doc.items.slice();
  if (live) {
    for (const p of live.values()) {
      if (byPid.has(p.id) || !p.bbox) continue;
      out.push({
        id: 'o-' + p.id.slice(0, 8), pid: p.id, code: p.code || undefined, name: String(p.name || '').normalize('NFC'),
        kind: KIND_OF_LAYER[p.layerNo] || 'qhpk', scale: p.scale || undefined, date: p.decisionDate || undefined,
        approver: p.approver || undefined, area: p.areaHa ?? undefined, pop: p.population ?? undefined,
        bbox: p.bbox, digitized: true, fresh: true,
      });
    }
  }
  return (merged = out);
}

/* ---------------- display helpers ---------------- */

const SCALE_RE = /,?\s*(?:tỷ|tỉ) lệ\s*1\s*\/\s*[\d.]+/i;
/** Short title for lists: "Phân khu đô thị N6", "Huyện Ba Vì", "Đô thị vệ tinh Sóc Sơn"… */
export function titleOf(e) {
  if (!e) return '';
  if (e.short) return e.short;
  if (e.kind === 'qhpk') return e.name.replace(/^Quy hoạch phân khu/i, 'Phân khu').replace(SCALE_RE, '').replace(/\s{2,}/g, ' ').trim();
  return e.name;
}
/** Label used by search results / the panel header (short code form for zone plans). */
export const pickLabel = (e) => (e.kind === 'qhpk' && e.code ? `Phân khu ${e.code}` : titleOf(e));
export const onMap = (e) => !!(e && (e.digitized || e.layer));
export const decisionText = (e) => (e.dec ? `${e.dec}${e.date ? ' ngày ' + e.date : ''}` : e.date ? `Phê duyệt ngày ${e.date}` : '');
/** "1/10.000" -> 10000 (for sorting). */
export const scaleValue = (s) => { const m = /1\s*\/\s*([\d.]+)/.exec(s || ''); return m ? +m[1].replace(/\./g, '') : NaN; };
/** "dd/mm/yyyy" -> yyyymmdd (for sorting). */
export const dateValue = (s) => { const m = /(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(s || ''); return m ? +m[3] * 10000 + +m[2] * 100 + +m[1] : NaN; };

/** Folded text a query is matched against. */
export function haystack(e) {
  if (!e._hay) {
    e._hay = fold([e.code, e.code ? 'phan khu ' + e.code : '', e.short, e.name, e.dec, e.date, e.addr, e.approver, KIND[e.kind]?.title].filter(Boolean).join(' · '));
    e._code = e.code ? normCode(e.code) : '';
  }
  return e._hay;
}

/** A point inside the project's largest part (the bbox centre can fall outside concave plans). */
export function insidePoint(e) {
  const p = liveProject(e);
  const polys = toPolygons(p?.geometry);
  if (polys.length) {
    let best = polys[0], ba = -1;
    for (const poly of polys) { const a = polygonArea(poly); if (a > ba) { ba = a; best = poly; } }
    const pt = interiorPoint(best);
    if (pt && Number.isFinite(pt[0])) return pt;
  }
  if (p?.center) return p.center;
  const b = e.bbox;
  return b ? [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2] : null;
}

/* ---------------- official records, amended ---------------- */

const sameScale = (a, b) => String(a || '').replace(/[.\s]/g, '') === String(b || '').replace(/[.\s]/g, '');

/**
 * The official project record with the catalogue's corrections (a few plans carry another plan's
 * code or a wrong scale on the official system) and its gaps filled from the published decision.
 * Returns a new object; `catalog` holds the entry (links, note), `official*` the replaced values.
 */
export function amendProject(p) {
  const e = p && entryOf(p.id);
  if (!e || p.catalog) return p;
  const q = { ...p, catalog: e };
  if (e.code && p.code && e.code !== p.code) { q.officialCode = p.code; q.code = e.code; }
  if (e.scale && p.scale && !sameScale(e.scale, p.scale)) { q.officialScale = p.scale; q.scale = e.scale; }
  if ((q.officialCode || q.officialScale) && e.name && e.name !== p.name) { q.officialName = p.name; q.name = e.name; }
  if (!q.scale && e.scale) q.scale = e.scale;
  if (!q.decision && e.dec) q.decision = e.dec;
  if (!q.decisionDate && e.date) q.decisionDate = e.date;
  if (!q.approver && e.approver) q.approver = e.approver;
  if (!Number.isFinite(q.areaHa) && Number.isFinite(e.area)) q.areaHa = e.area;
  if (!Number.isFinite(q.population) && Number.isFinite(e.pop)) { q.population = e.pop; q.popYear = e.popYear; }
  if (!q.location && e.addr) q.location = e.addr;
  return q;
}

/** Amend every level's project of a point result in place (no-op until the catalogue is loaded). */
export function amendResult(r) {
  if (!doc || !r?.levels) return false;
  let changed = false;
  for (const l of r.levels) {
    if (!l.project || l.project.catalog) continue;
    const q = amendProject(l.project);
    if (q !== l.project) { l.project = q; changed = true; }
  }
  return changed;
}
